<script setup>
import { getProviderCompany, searchProviderCompanies } from '@/services/manage/companyService.js'
import { formatEntityOption } from './services.js'
import { computed, ref } from 'vue'
import { asyncComputed } from '@/utils/asyncComputed.js'
import MtModal from '@/components/shared/modal.vue'
import MtInput from '@/components/shared/mt-input.vue'

const props = defineProps({
    cik: {
        type: [String, Number],
        required: true,
    },
    edit: {
        type: Boolean,
        default: false,
        required: false,
    },
    value: {
        type: [String],
        required: false,
    },
})

const emit = defineEmits(['input'])

const cikMatches = asyncComputed(async () => {
    return (await searchProviderCompanies(1, `!cik:${props.cik}`, 100, true)).map(i => ({
        factSetEntityId: i.providerEntityId,
        display: formatEntityOption(i),
        name: i.name,
    }))
}, null)

const issuers = asyncComputed(async () => {
    const options = cikMatches.value

    if (!cikMatches.value) {
        return
    }

    if (props.value && !options.find(o => o.factSetEntityId === props.value)) {
        const entity = await getProviderCompany(1, props.value)

        if (entity) {
            options.push({
                factSetEntityId: entity.providerEntityId,
                display: formatEntityOption(entity),
                name: entity.name,
            })
        }
    }

    if (props.value && !options.find(o => o.factSetEntityId === props.value)) {
        options.push({
            factSetEntityId: props.value,
            display: `<unknown> [${props.value}]`,
            name: '',
        })
    }

    options.sort((lhs, rhs) => lhs.name.localeCompare(rhs.name))

    return options
}, [])

const issuerDisplay = computed(
    () => issuers.value.find(i => i.factSetEntityId === props.factSetEntityId)?.display ?? '-'
)

const OTHER_VALUE = '_OTHER_'

const boundValue = computed({
    get() {
        return other.value ? OTHER_VALUE : props.value
    },
    set(v) {
        if (v !== OTHER_VALUE) {
            emit('input', v)
            return true
        } else {
            other.value = true
            return false
        }
    },
})

const other = ref(false)
const otherFactSetEntityId = ref(null)

const otherFactSetEntity = asyncComputed(async () => {
    if (otherFactSetEntityId.value?.length === 8) {
        return getProviderCompany(1, otherFactSetEntityId.value)
    }
    return null
}, null)

function setOther() {
    boundValue.value = otherFactSetEntityId.value.toUpperCase()
    clearOther()
}

function clearOther() {
    other.value = null
    otherFactSetEntityId.value = null
}
</script>
<template>
    <div>
        <select v-model="boundValue" v-if="edit">
            <option :value="null">-</option>
            <option v-for="issuer in issuers" :key="issuer.factSetEntityId" :value="issuer.factSetEntityId">
                {{ issuer.display }}
            </option>
            <option :value="OTHER_VALUE">Other...</option>
        </select>
        <span v-else>
            {{ issuerDisplay }}
        </span>
        <MtModal v-if="other" @dismiss="clearOther">
            <MtInput label="FactSetEntityId" v-model="otherFactSetEntityId" />
            <table class="mt-table" v-if="otherFactSetEntity">
                <tr>
                    <th>Name</th>
                    <td>{{ otherFactSetEntity?.name }}</td>
                </tr>
                <tr>
                    <th>Symbol</th>
                    <td>{{ otherFactSetEntity?.symbol }}-{{ otherFactSetEntity?.securityCountry }}</td>
                </tr>
                <tr>
                    <th>Type</th>
                    <td>{{ otherFactSetEntity?.companyType }}</td>
                </tr>
                <tr>
                    <th>Sector</th>
                    <td>{{ otherFactSetEntity?.sector }}</td>
                </tr>
                <tr>
                    <th>Industry</th>
                    <td>{{ otherFactSetEntity?.industry }}</td>
                </tr>
            </table>
            <div class="button-group">
                <button class="secondary" @click.prevent="clearOther">Cancel</button>
                <button class="primary" @click.prevent="setOther" :disabled="!otherFactSetEntity">Save</button>
            </div>
        </MtModal>
    </div>
</template>
